import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/components/Patient/Patient.constants';
import { FF_IMMUTABLE_CLINIC_NOTES } from '@/constants/featureFlags';
import FetchFailedError from '@/error-handling/components/FetchFailedError';
import { useFeatureFlags } from '@/hooks';
import type { ResponseCursors } from '@/types';
import { LUXON_FORMAT_DAY_MONTH_YEAR, LUXON_FORMAT_HOUR_MINUTE_AM_PM } from '@/utils/luxon';
import type { PaginationModel } from '@montugroup/design-system';
import { Button, Link, PaginationVariant, Table, TextField, Tooltip, Typography } from '@montugroup/design-system';
import CancelIcon from '@mui/icons-material/Block';

import { CopyToClipboardButton } from '@/components/Patient/components/CopyToClipboardButton';
import { Box, Stack, debounce } from '@mui/material';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

// TODO: Identify what the actual type of this is from PMS service, and move to MGS.
export type PatientNoteData = {
  CreatedBy: {
    email: string;
    first_name: string;
    last_name: string;
  };
  id: number;
  active?: boolean;
  created_by: number;
  created_date: string; // ISODate string
  modified_by: number;
  modified_date: string; // ISODate string
  heading: string;
  note?: string;
  patient_id: number;
  strike_reason?: string;
};

export type PatientNotesProps = {
  notes: PatientNoteData[];
  isAddNoteDisabled?: boolean;
  onViewNote: (selectedNote: PatientNoteData) => void;
  onAddNewNote: () => void;
  handleNotesPaginationChange?: ({ page, pageSize }: PaginationModel) => void;
  responseNotesCursors?: ResponseCursors;
  isPatientNotesLoading?: boolean;
  isPatientNotesError?: boolean;
  setNotesSearch?: Dispatch<SetStateAction<string>>;
  onSort?: (sort: { id: string; desc: boolean }[]) => void;
  setNotesCursors?: Dispatch<SetStateAction<ResponseCursors>>;
};

export const PatientNotes = ({
  notes,
  onViewNote,
  onAddNewNote,
  isAddNoteDisabled,
  handleNotesPaginationChange,
  responseNotesCursors,
  setNotesCursors,
  setNotesSearch,
  onSort,
  isPatientNotesError,
}: PatientNotesProps) => {
  const { flags } = useFeatureFlags();
  const ffImmutableNotesActive = flags[FF_IMMUTABLE_CLINIC_NOTES];
  const [searchFilterText, setSearchFilterText] = useState('');
  const columns: ColumnDef<PatientNoteData, any>[] = [
    {
      id: 'status',
      header: () => (
        <Tooltip title="Status">
          <Box>Status</Box>
        </Tooltip>
      ),
      cell: ({ row }) => (
        <Box>{ffImmutableNotesActive && row.original?.strike_reason ? <CancelIcon color="disabled" /> : '-'}</Box>
      ),
      enableSorting: false,
      meta: {
        sx: {
          width: '50px',
        },
      },
    },
    {
      accessorFn: ({ created_date }) => new Date(created_date),
      enableSorting: true,
      id: 'createdDate',
      meta: {
        sx: {
          width: '100px',
        },
      },
      header: () => (
        <Tooltip title="Created Date">
          <Box>Created</Box>
        </Tooltip>
      ),
      cell: ({ row, getValue }: CellContext<PatientNoteData, Date>) => (
        <Link
          component="button"
          {...(ffImmutableNotesActive &&
            Boolean(row.original?.strike_reason) && {
              color: 'action.disabled',
            })}
          sx={{
            cursor: 'pointer',
          }}
          data-testid={`viewNote-${row.original.id}`}
          onClick={() => onViewNote(row.original)}
        >
          <Typography variant="body2" component="span">
            {DateTime.fromJSDate(getValue()).toFormat(LUXON_FORMAT_DAY_MONTH_YEAR)}
          </Typography>{' '}
          <Typography variant="body2" component="span">
            {DateTime.fromJSDate(getValue()).toFormat(LUXON_FORMAT_HOUR_MINUTE_AM_PM)}
          </Typography>
        </Link>
      ),
    },
    {
      accessorKey: 'heading',
      id: 'heading',
      enableSorting: true,
      header: () => (
        <Tooltip title="Note Header">
          <Box>Note Header</Box>
        </Tooltip>
      ),
    },
    {
      id: 'createdBy',
      enableSorting: false,
      accessorFn: ({ CreatedBy }) => `${CreatedBy.first_name} ${CreatedBy.last_name}`,
      header: () => (
        <Tooltip title="Created By">
          <Box>Created By</Box>
        </Tooltip>
      ),
    },
    {
      id: 'copyNote',
      header: () => (
        <Tooltip title="Copy Note">
          <Box>Copy Note</Box>
        </Tooltip>
      ),
      cell: ({ row }) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CopyToClipboardButton text={row.original.note ?? ''} label={`Copy note ${row.original.id}`} />
        </Box>
      ),
      enableSorting: false,
      meta: { sx: { width: '100px' } },
    },
  ];

  const handleSearchOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value;
    setSearchFilterText(searchValue);

    setNotesCursors?.({ prev: null, next: null });
    setNotesSearch?.(searchValue);
  };
  return (
    <Stack>
      <Stack
        flexDirection={{ xs: 'column', md: 'row' }}
        alignItems="center"
        justifyContent="space-between"
        paddingBottom={3}
      >
        <TextField
          label="Search"
          variant="outlined"
          onChange={debounce(handleSearchOnChange, 1000)}
          size="small"
          sx={{ minWidth: (t) => t.spacing(66), width: { xs: '100%', lg: 'auto' } }}
        />
        <Button
          data-testid="addNote"
          size="large"
          onClick={() => onAddNewNote?.()}
          color="primary"
          disabled={isAddNoteDisabled}
          sx={{ alignSelf: 'stretch' }}
        >
          Add Note
        </Button>
      </Stack>

      {isPatientNotesError ? (
        <FetchFailedError />
      ) : (
        <Box
          component={Table}
          columns={columns}
          data={notes}
          hasRowBackgroundColor={false}
          showPagination={true}
          pageSize={DEFAULT_PAGE_SIZE}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          globalFilter={searchFilterText}
          sx={{
            width: '100%',
          }}
          applyRowStyles={(row: { original: PatientNoteData } | unknown) => {
            if (
              typeof row === 'object' &&
              row !== null &&
              'original' in row &&
              ffImmutableNotesActive &&
              (row.original as PatientNoteData).strike_reason
            ) {
              return {
                td: {
                  color: 'action.disabled',
                },
              };
            }
            return {};
          }}
          onSortingChange={onSort}
          onPaginationModelChange={handleNotesPaginationChange}
          paginationVariant={PaginationVariant.UNKNOWN_PAGE_SIZE}
          hasPrevPage={Boolean(responseNotesCursors?.prev)}
          hasNextPage={Boolean(responseNotesCursors?.next)}
        />
      )}
    </Stack>
  );
};
